import type { AnalyticsConfig, AnalyticsTabKey } from './analyticsConfig';
import type { AssociationRow } from './analyticsTypes';
import { getRowFieldValue } from './analyticsHelpers';
import { getMetricCatalog, type MetricDefinition } from './metricCatalog';

export type ChartableKind = 'categorical' | 'numeric' | 'year';

export interface ChartableColumn {
  id: string;
  field: string;
  label: string;
  kind: ChartableKind;
  layout: 'horizontalBar' | 'verticalBar' | 'distribution';
  tabs?: AnalyticsTabKey[];
}

const CURATED_COLUMNS: ChartableColumn[] = [
  {
    id: 'associationType',
    field: 'associationType',
    label: 'Association type',
    kind: 'categorical',
    layout: 'horizontalBar',
    tabs: ['GDA', 'VDA'],
  },
  {
    id: 'EL',
    field: 'EL',
    label: 'Evidence level (EL)',
    kind: 'categorical',
    layout: 'horizontalBar',
    tabs: ['GDA'],
  },
  {
    id: 'diseaseType',
    field: 'diseaseType',
    label: 'Disease type',
    kind: 'categorical',
    layout: 'horizontalBar',
  },
  {
    id: 'geneNcbiType',
    field: 'geneNcbiType',
    label: 'Gene type',
    kind: 'categorical',
    layout: 'horizontalBar',
    tabs: ['GDA'],
  },
  {
    id: 'diseaseClassName_MSH',
    field: 'diseaseClassName_MSH',
    label: 'Disease class (MeSH)',
    kind: 'categorical',
    layout: 'horizontalBar',
  },
  {
    id: 'geneProteinClassNames',
    field: 'geneProteinClassNames',
    label: 'Protein class',
    kind: 'categorical',
    layout: 'horizontalBar',
    tabs: ['GDA'],
  },
  {
    id: 'yearInitial',
    field: 'yearInitial',
    label: 'Year first linked',
    kind: 'year',
    layout: 'verticalBar',
    tabs: ['GDA', 'VDA'],
  },
  {
    id: 'yearFinal',
    field: 'yearFinal',
    label: 'Year last evidence',
    kind: 'year',
    layout: 'verticalBar',
    tabs: ['GDA', 'VDA'],
  },
];

const CURATED_LABELS = new Map(CURATED_COLUMNS.map(column => [column.field, column.label]));
const NON_CHARTABLE_FIELD = /^(id|_.*)$|(Ids?|IDs?|CUI|Symbol|Description|Sentence|Timeline|Url|Link)$/;

const MAX_SCALAR_CATEGORIES = 40;
const MAX_SCALAR_CATEGORY_SHARE = 0.3;
const MIN_NUMERIC_SHARE = 0.9;

function metricToChartable(metric: MetricDefinition): ChartableColumn {
  return {
    id: metric.id,
    field: metric.field,
    label: metric.label,
    kind: 'numeric',
    layout: 'distribution',
  };
}

function isAllowedForTab(column: ChartableColumn, tab: AnalyticsTabKey): boolean {
  if (!column.tabs) return true;
  return column.tabs.includes(tab);
}

export function humanizeFieldName(field: string): string {
  const spaced = field
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (!spaced) return field;
  return spaced
    .split(/\s+/)
    .map((word, index) => {
      const isAcronym = word.length > 1 && word === word.toUpperCase();
      const text = isAcronym ? word : word.toLowerCase();
      return index === 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text;
    })
    .join(' ');
}

interface FieldStats {
  nonNull: number;
  numeric: number;
  years: number;
  isArray: boolean;
  hasObject: boolean;
  distinct: Set<string>;
}

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  const text = String(value).trim();
  return text === '' || text === '-' || text.toLowerCase() === 'null';
}

function collectFieldStats(data: AssociationRow[]): Map<string, FieldStats> {
  const stats = new Map<string, FieldStats>();

  data.forEach(row => {
    Object.entries(row).forEach(([field, value]) => {
      let entry = stats.get(field);
      if (!entry) {
        entry = {
          nonNull: 0,
          numeric: 0,
          years: 0,
          isArray: false,
          hasObject: false,
          distinct: new Set<string>(),
        };
        stats.set(field, entry);
      }

      const values: unknown[] = Array.isArray(value) ? value : [value];
      if (Array.isArray(value)) entry.isArray = true;
      if (values.some(item => item !== null && typeof item === 'object')) {
        entry.hasObject = true;
        return;
      }

      const present = values.filter(item => !isBlank(item));
      if (!present.length) return;
      entry.nonNull += 1;

      if (Array.isArray(value)) {
        present.forEach(item => entry!.distinct.add(String(item).trim()));
        return;
      }

      const scalar = present[0];
      const asNumber = typeof scalar === 'number' ? scalar : Number(String(scalar).trim());
      if (Number.isFinite(asNumber)) {
        entry.numeric += 1;
        if (Number.isInteger(asNumber) && asNumber >= 1900 && asNumber <= 2100) entry.years += 1;
      }
      entry.distinct.add(String(scalar).trim());
    });
  });

  return stats;
}

function classifyField(field: string, stats: FieldStats): ChartableKind | null {
  if (stats.hasObject || stats.nonNull < 2) return null;
  if (NON_CHARTABLE_FIELD.test(field)) return null;
  if (stats.distinct.size < 2) return null;

  if (stats.isArray) return 'categorical';

  if (stats.numeric / stats.nonNull >= MIN_NUMERIC_SHARE) {
    if (/year/i.test(field) && stats.years / stats.nonNull >= MIN_NUMERIC_SHARE) return 'year';
    return 'numeric';
  }

  const distinct = stats.distinct.size;
  const isFewValues = distinct <= MAX_SCALAR_CATEGORIES && distinct / stats.nonNull <= 0.6;
  const isRepeatedEnough =
    distinct <= MAX_SCALAR_CATEGORIES * 2 && distinct / stats.nonNull <= MAX_SCALAR_CATEGORY_SHARE;
  return isFewValues || isRepeatedEnough ? 'categorical' : null;
}

export function discoverChartableColumns(data: AssociationRow[]): ChartableColumn[] {
  if (data.length < 2) return [];

  const found: ChartableColumn[] = [];
  collectFieldStats(data).forEach((stats, field) => {
    const kind = classifyField(field, stats);
    if (!kind) return;
    found.push({
      id: field,
      field,
      label: CURATED_LABELS.get(field) ?? humanizeFieldName(field),
      kind,
      layout: kind === 'categorical' ? 'horizontalBar' : kind === 'year' ? 'verticalBar' : 'distribution',
    });
  });

  return found;
}

export function getChartableColumns(
  config: AnalyticsConfig,
  view: string,
  data: AssociationRow[] = [],
): ChartableColumn[] {
  const byField = new Map<string, ChartableColumn>();
  const add = (column: ChartableColumn) => {
    if (!byField.has(column.field)) byField.set(column.field, column);
  };

  CURATED_COLUMNS.filter(column => isAllowedForTab(column, config.tab)).forEach(add);
  getMetricCatalog(config, view).map(metricToChartable).forEach(add);
  discoverChartableColumns(data).forEach(add);

  return [...byField.values()];
}

export function getChartableByField(
  columns: ChartableColumn[],
  field: string | undefined | null,
): ChartableColumn | undefined {
  if (!field) return undefined;
  return columns.find(entry => entry.field === field || entry.id === field);
}

export function hasChartableFieldData(data: AssociationRow[], column: ChartableColumn): boolean {
  if (!data.length) return true;

  if (column.kind === 'numeric' || column.kind === 'year') {
    return data.some(row => Number.isFinite(Number(getRowFieldValue(row, column.field))));
  }

  return data.some(row => {
    const value = row[column.field];
    if (Array.isArray(value)) return value.some(entry => !isBlank(entry));
    return !isBlank(value);
  });
}

export function getAvailableChartableColumns(
  columns: ChartableColumn[],
  data: AssociationRow[],
): ChartableColumn[] {
  if (!data.length) return columns;
  return columns.filter(column => hasChartableFieldData(data, column));
}

export function resolveColumnChartTitle(column: ChartableColumn): string {
  return column.label;
}
